import { groupBy } from "lodash-es";
import { DtoCubeDataResponse, DtoEntry, DtoTestSet } from "@/types/dto-types";
import { CalculationType, Category } from "../../types";
import { OverviewData } from "./../useCubeOverviewData";
import { CubeSideName } from "../../components/Сube/types";
import {
  CategoryScoreValues,
  TestSetWithScore,
  TopicWithScore,
} from "./../useHomePageData/types";
import {
  getAverageScaleValue,
  getScalesValueKeysByCategory,
  normalizeValueToPercentage,
} from "../useHydratedCubeData/utils";
import { Category as ScaleCategory } from "../useHydratedCubeData/types";
import { getBenchmarkValue } from "../../utils";

export function filterRawDataByCategory(
  category: Category,
  rawData?: DtoCubeDataResponse
) {
  if (!rawData) return undefined;

  const currentCategoryTestSets = rawData.testSets.filter(
    ts => ts.category === category
  );
  const currentCategoryTestSetsIds = currentCategoryTestSets.map(ts => ts._id);

  return {
    ...rawData,
    testSets: currentCategoryTestSets,
    allEntries: rawData.allEntries.filter(entry =>
      currentCategoryTestSetsIds.includes(entry.testSetId)
    ),
  };
}

export function getAverageObject(acc: Record<CubeSideName, number>) {
  const values = Object.values(acc).filter(v => v !== 0);
  if (values.length === 0) return 0;
  const sum = values.reduce((a, b) => a + b, 0);
  return sum / values.length;
}

export function getAverageOfSidesValues(
  overviewData: OverviewData
): CategoryScoreValues {
  return {
    [CalculationType.Benchmark]: {
      percentageValue: getAverageObject(
        overviewData[CalculationType.Benchmark].percentageValue
      ),
      cubeSideFillArea: getAverageObject(
        overviewData[CalculationType.Benchmark].cubeSideFillArea
      ),
    },
    [CalculationType.AverageScore]: {
      percentageValue: getAverageObject(
        overviewData[CalculationType.AverageScore].percentageValue
      ),
      cubeSideFillArea: getAverageObject(
        overviewData[CalculationType.AverageScore].cubeSideFillArea
      ),
    },
    averageScore: overviewData.averageScore,
    averageBenchmark: overviewData.averageBenchmark,
    scoreVsBenchmarkDifference:
      overviewData.averageScore - overviewData.averageBenchmark,
  };
}
export function getTestSetsWithScoresAggregated(
  testSets: DtoTestSet[],
  entries: DtoEntry[],
  currentTestId: string,
): TestSetWithScore[] {
  const groupedTestSets = Object.values(
    groupBy(testSets, ts => `${ts.info.cubeSide ?? ""}|${ts.category}`)
  );

  return groupedTestSets.flatMap(group => {
    const currentTestSet = group.find(ts => ts.testId === currentTestId);
    if (!currentTestSet) return [];

    const validScores = getTestSetsWithScores(group, entries)
      .map(ts => ts.score)
      .filter(score => !Number.isNaN(score));

    if (validScores.length === 0) return [];

    const averageScore =
      validScores.reduce((sum, score) => sum + score, 0) / validScores.length;

    return [{ ...currentTestSet, score: averageScore }];
  });
}

export function getTestSetsWithScores(
  testSets: DtoTestSet[],
  entries: DtoEntry[]
): TestSetWithScore[] {
  return testSets.map(testSet => {
    const consentScaleKeys = getScalesValueKeysByCategory(
      testSet.scales,
      ScaleCategory.Consent
    );

    const testSetEntries = entries.filter(
      entry => entry.testSetId === testSet._id
    );

    const totalValue = testSetEntries.reduce((acc, entry) => {
      const consentValue = getAverageScaleValue(consentScaleKeys, entry);
      return acc + consentValue;
    }, 0);

    const score = normalizeValueToPercentage(
      testSetEntries.length > 0
        ? totalValue / testSetEntries.length
        : Number.NaN
    );

    return {
      ...testSet,
      score,
    };
  });
}

export function getTopicsWithScore(
  currentTestId: string,
  rawData: DtoCubeDataResponse | undefined,
  filteredEntries: DtoEntry[],
  benchmarkTitle: string | undefined,
  order: "ASC" | "DESC" | undefined,
  numberOfTopics: number | undefined,
  aggregatesData: boolean | undefined
) {
  const currentTest = rawData?.tests.find(test => test._id === currentTestId);
  if (!currentTest) return [];

  const testSets = rawData!.testSets.filter(
    ts =>
      ts.category === Category.Team ||
      ts.category === Category.Organization ||
      ts.category === Category.Leadership
  );

  const testSetsWithScores = aggregatesData ? getTestSetsWithScoresAggregated(
    testSets,
    filteredEntries,
    currentTestId,
  ) : getTestSetsWithScores(
    testSets,
    filteredEntries,
  );

  let result = testSetsWithScores
    .filter(ts => !Number.isNaN(ts.score))
    .map<TopicWithScore>(ts => {
      const benchmarkValue = getBenchmarkValue(ts, benchmarkTitle);
      const benchmark = normalizeValueToPercentage(benchmarkValue ?? 0);
      return {
        ...ts,
        benchmark,
        scoreVsBenchmarkDifference: ts.score - benchmark,
        cubeSide: parseInt(ts.info.cubeSide ?? "", 10),
        category: ts.category as Category,
      };
    });

  if (order) {
    result = result.sort((a, b) =>
      order === "ASC"
        ? a.scoreVsBenchmarkDifference - b.scoreVsBenchmarkDifference
        : b.scoreVsBenchmarkDifference - a.scoreVsBenchmarkDifference
    );
  }

  if (numberOfTopics) {
    result = result.slice(0, numberOfTopics);
  }

  return result;
}
